'use client';

import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';
import type { FortuneWheelApi } from '@/api/cms';
import { ImageComponent } from '@/components/Image';
import {
  getStatusByTimeRange,
  ReceiveBonusesNotice,
  ScheduleStatus,
  useCMSConfig,
} from '@/modules/common';
import { CountdownWidget } from '@/modules/common/components/Countdown/CountdownWidget';
import { FAQByGroup } from '@/modules/restrictions/components/FAQByGroup';
import { StepsByGroup } from '@/modules/restrictions/components/StepsByGroup';
import { TextContentByGroup } from '@/modules/restrictions/components/TextContentByGroup';
import { useGetCommonStore } from '@/store/commonStore';
import { ScContainer, Spinner } from '@/styles/common';
import { Wheel } from './components/Wheel';
import { SpinsCounter } from './components/Wheel/components/SpinsCounter';
import {
  ScWheelContent,
  ScWheelContentSection,
  ScWheelHeader,
  ScWheelHeaderAssets,
  ScWheelPage,
} from './styled';

interface FortuneWheelPageProps {
  data: FortuneWheelApi;
}

const FortuneWheelPage: FC<FortuneWheelPageProps> = ({ data }) => {
  const { t } = useTranslation('wheel');
  const useCommonStore = useGetCommonStore();

  const {
    pageTitle,
    announceDescription,
    description,
    wheelArrow,
    bonusBuyPromocode,
    wheel,
    topBg,
    leftDecor,
    rightDecor,
    mobileCharacter,
    leftCharacter,
    rightCharacter,
    howToParticipate,
    faq,
    contentBg,
    contentLeftCharacter,
    contentRightCharacter,
  } = data;

  const { wheelLootboxes, isAuthenticated, isMobileDevice } = useCommonStore(
    useShallow((state) => ({
      wheelLootboxes: state.wheelLootboxes,
      isAuthenticated: state.isAuthenticated,
      isMobileDevice: state.isMobileDevice,
    }))
  );
  const { blackColor, mainColor } = useTheme();

  const { cmsConfig } = useCMSConfig();

  const labelTextMap = {
    [ScheduleStatus.ACTIVE]: 'endsIn',
    [ScheduleStatus.COMING_SOON]: 'startsIn',
    [ScheduleStatus.FINISHED]: 'finished',
  };

  const [timeLeft, mode] = useMemo(
    () =>
      getStatusByTimeRange(
        String(cmsConfig?.fortuneWheel.dateRange?.start),
        String(cmsConfig?.fortuneWheel.dateRange?.end)
      ),
    [
      cmsConfig?.fortuneWheel.dateRange?.start,
      cmsConfig?.fortuneWheel.dateRange?.end,
    ]
  );

  const isComingSoon = mode === ScheduleStatus.COMING_SOON;

  return (
    <ScWheelPage>
      <div className="bonuses-notice-box">
        <ReceiveBonusesNotice
          successMessage="canIssueBonuses.success"
          translation="promotions"
          warningMessage="canIssueBonuses.warning"
        />
      </div>
      {wheelLootboxes ? (
        <>
          <ScWheelHeader>
            <div className="content">
              <ScContainer>
                <h1>{pageTitle}</h1>
                {isComingSoon ? (
                  <p
                    className="description"
                    dangerouslySetInnerHTML={{ __html: announceDescription }}
                  />
                ) : (
                  <TextContentByGroup
                    className="description"
                    sections={description}
                  />
                )}

                <div className="countdown-wrapper">
                  <CountdownWidget
                    label={t(labelTextMap[mode])}
                    showCountdown={mode !== ScheduleStatus.FINISHED}
                    timeLeft={Math.floor(timeLeft / 1e3)}
                    withBg
                  />
                </div>
                {!isComingSoon && (
                  <div className="spins-counter-mob">
                    <SpinsCounter />
                  </div>
                )}
              </ScContainer>
            </div>

            <Wheel
              arrowImg={wheelArrow?.url || ''}
              bonusBuyPromocode={bonusBuyPromocode}
              lootboxes={wheelLootboxes}
              mode={mode}
              wheelImages={wheel}
            />
            {wheel?.stand && (
              <ImageComponent
                alt="wheel-stand"
                className="wheel-stand"
                height={300}
                src={wheel?.stand?.url}
                width={354}
              />
            )}
            {!isComingSoon && (
              <div className="spins-counter-desk">
                <SpinsCounter />
              </div>
            )}

            <ScWheelHeaderAssets>
              <ImageComponent
                alt="background"
                className="bg"
                fill
                src={topBg?.url || ''}
              />
              {leftDecor && (
                <ImageComponent
                  alt="left decor"
                  className="decor left"
                  height={260}
                  src={leftDecor?.url || ''}
                  width={260}
                />
              )}

              {rightDecor &&
                (isMobileDevice && !isAuthenticated ? null : (
                  <ImageComponent
                    alt="right decor"
                    className="decor right"
                    height={164}
                    src={rightDecor?.url || ''}
                    width={164}
                  />
                ))}

              {isMobileDevice ? (
                <ImageComponent
                  alt="mobile character"
                  className="mobile static"
                  height={261}
                  src={mobileCharacter?.url || ''}
                  width={376}
                />
              ) : (
                <>
                  <ImageComponent
                    alt="left character"
                    className="left static"
                    height={600}
                    src={leftCharacter?.url || ''}
                    width={533}
                  />
                  <ImageComponent
                    alt="right character"
                    className="right static"
                    height={656}
                    src={rightCharacter?.url || ''}
                    width={469}
                  />
                </>
              )}
            </ScWheelHeaderAssets>
          </ScWheelHeader>

          <ScWheelContentSection>
            {contentBg?.url && (
              <ImageComponent
                alt="center"
                className="center"
                height={398}
                src={contentBg.url}
                width={578}
              />
            )}
            {contentLeftCharacter?.url && (
              <ImageComponent
                alt="left character"
                className="left"
                height={432}
                src={contentLeftCharacter.url}
                width={243}
              />
            )}
            {contentRightCharacter?.url && (
              <ImageComponent
                alt="right character"
                className="right"
                height={432}
                src={contentRightCharacter.url}
                width={243}
              />
            )}
            <ScContainer>
              <ScWheelContent>
                <StepsByGroup
                  hideDescription
                  imgSize={{ height: 98, width: 84 }}
                  sections={howToParticipate}
                />
                <FAQByGroup sections={faq} />
              </ScWheelContent>
            </ScContainer>
          </ScWheelContentSection>
        </>
      ) : (
        <Spinner background={blackColor} color={mainColor} size={50} />
      )}
    </ScWheelPage>
  );
};

export default FortuneWheelPage;
